<template>
    <div class="viewport">
        <md-list class="md-double-line" style="padding-top: 0">
            <md-divider></md-divider>
            <md-toolbar :md-elevation="1">
                <span class="md-title">Werkbon info</span>
            </md-toolbar>
            <md-list-item>
                <div class="md-layout-item">
                    <div class="md-layout-item">
                        <label style="font-weight: bold">
                            <p style="margin: 0px">
                                Werkbon nummer: {{ workOrderUpsert.work_order_id ? workOrderUpsert.work_order_id : '' }}
                            </p>
                            <p style="margin: 0px" v-if="workOrderUpsert.status">
                                Status: {{ workOrderUpsert.status ? workOrderUpsert.status : '' }}
                            </p>
                        </label>
                    </div>
                </div>
                <div class="md-layout-item">
                    <div class="md-layout-item" style="text-align: right">
                        <label class="font-weight-bold">
                            Datum:
                            {{
                                workOrderUpsert.creation_date
                                    ? workOrderUpsert.creation_date.toLocaleDateString()
                                    : new Date().toLocaleDateString()
                            }}
                        </label>
                    </div>
                </div>
            </md-list-item>
            <md-list-item class="md-double-line">
                <div class="md-layout-item">
                    <md-field>
                        <label>Werkbon type</label>
                        <md-select
                            type="text"
                            name="work_order_type"
                            id="work_order_type"
                            v-model="workOrderUpsert.work_order_type_id"
                        >
                            <md-option
                                v-for="workOrderType in workOrderTypes"
                                :key="workOrderType.work_order_type_id"
                                :value="workOrderType.work_order_type_id"
                                >{{ workOrderType.name }}</md-option
                            >
                        </md-select>
                    </md-field>
                </div>
                <div class="md-layout-item md-list-action">
                    <md-field>
                        <label>Taal</label>
                        <md-select
                            name="work-order-language"
                            id="work-order-language"
                            v-model="workOrderUpsert.data.language"
                        >
                            <md-option :key="Language.nl" :value="Language.nl">NL</md-option>
                            <md-option :key="Language.fr" :value="Language.fr">FR</md-option>
                        </md-select>
                    </md-field>
                </div>
            </md-list-item>
            <md-list-item>
                <div class="md-layout-item">
                    <md-field>
                        <label>Technieker</label>
                        <md-select
                            type="text"
                            @md-selected="selectTechnicianUser"
                            name="work_order_technician"
                            id="work_order_technician"
                            v-model="selectedTechnicianIndex"
                        >
                            <md-option :key="i" v-for="(user, i) of workOrderUsers" :value="i">{{
                                `${user.first_name} ${user.last_name}`
                            }}</md-option>
                        </md-select>
                    </md-field>
                </div>
                <div class="md-layout-item md-list-action" v-if="showTravelDistance">
                    <md-field>
                        <label>Verplaatsing forfaitair</label>
                        <md-select
                            name="work-order-travel_distance"
                            id="work-order-travel_distance"
                            v-model="workOrderUpsert.data.travel_distance"
                        >
                            <md-option :key="1" :value="1">Zone 1</md-option>
                            <md-option :key="2" :value="2">Zone 2</md-option>
                        </md-select>
                    </md-field>
                </div>
            </md-list-item>
            <div>
                <div v-if="checklistItems.length > 0">
                    <WorkOrderTypeAdditionalChecks :workOrderUpsert="workOrderUpsert" :items="checklistItems" />
                </div>
            </div>
        </md-list>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IUserEntity, Language } from '@phrospr/api-backend';
import { workOrderStore } from '@/store/work-order/workOrder.store';
import {
    IConfiguration,
    IWorkOrderChecklistItemEntity,
    IWorkOrderEntity,
    IWorkOrderTypeEntity,
} from '@phrospr/lib-models';
import { selectableTypes } from '@phrospr/lib-core/src/engine/type-catalogue.engine';
import { resolveChecklistItems } from '@phrospr/lib-core/src/engine/work-order-checklist.engine';
import WorkOrderTypeAdditionalChecks from '@/views/work-order/components/WorkOrderTypeAdditionalChecks.vue';

@Component({
    components: {
        WorkOrderTypeAdditionalChecks,
    },
})
export default class WorkOrderInfo extends Vue {
    @Prop() private workOrderUpsert!: IWorkOrderEntity;

    get workOrderUsers(): IUserEntity[] {
        return workOrderStore.state.workOrderUsers;
    }

    get workOrderTypes(): IWorkOrderTypeEntity[] {
        return selectableTypes(
            workOrderStore.state.workOrderTypes,
            type => type.work_order_type_id,
            this.workOrderUpsert.work_order_type_id,
        );
    }

    get configuration(): Partial<IConfiguration> {
        return this.$store.state.configuration;
    }

    get showTravelDistance(): boolean {
        // Missing config keeps today's behavior (field visible); tenants opt out explicitly.
        return this.configuration?.workOrders?.travelDistanceEnabled !== false;
    }

    // The type's checklist, plus anything this work order already answered.
    get checklistItems(): IWorkOrderChecklistItemEntity[] {
        const selected = workOrderStore.state.workOrderTypes.find(
            x => x.work_order_type_id === this.workOrderUpsert.work_order_type_id,
        );
        return resolveChecklistItems(
            selected?.work_order_checklist_id,
            workOrderStore.state.workOrderChecklists,
            this.workOrderUpsert.data.additional_checks,
        );
    }

    Language = Language;
    sold = false;
    sendEmailToCustomer = true;
    selectedTechnicianIndex: number = null;

    constructor() {
        super();
        if (this.workOrderUpsert.work_order_id === null) {
            this.workOrderUpsert.data.language = Language.nl;
            this.workOrderUpsert.data.travel_distance = 1;
        }
        this.load();
    }

    private async load() {
        await Promise.all([
            workOrderStore.dispatch('loadWorkOrderUsers'),
            workOrderStore.dispatch('loadWorkOrderTypes'),
            workOrderStore.dispatch('loadWorkOrderChecklists'),
        ]);
        if (this.workOrderUpsert.work_order_id === null) {
            if (this.workOrderUpsert.work_order_type_id == null && this.workOrderTypes.length) {
                this.workOrderUpsert.work_order_type_id = this.workOrderTypes[0].work_order_type_id;
            }
        } else {
            if (typeof this.workOrderUpsert.data.work_order_technician_user_id === 'number')
                this.selectedTechnicianIndex = this.workOrderUsers.findIndex(
                    x => x.user_id === this.workOrderUpsert.data.work_order_technician_user_id,
                );
        }
    }

    selectTechnicianUser(userListIndex: number) {
        const user = this.workOrderUsers[userListIndex];
        this.workOrderUpsert.data.work_order_technician_user_id = user.user_id;
    }
}
</script>

<style lang="scss" scoped></style>
